import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { menuData } from 'Layout/LayoutMenuData';

interface MenuItem {
  id?: string;
  label: string;
  link?: string;
  icon?: React.ReactNode;
  isTitle?: boolean;
  subItems?: MenuItem[];
  parentId?: string;
}

const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const location = useLocation();

  const toggleMenu = (id: string) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedMenus(newExpanded);
  };

  const isMenuActive = (link?: string): boolean => {
    if (!link) return false;
    if (link === '/#') return false;
    return location.pathname === link;
  };

  const renderMenuItems = (items: MenuItem[], level = 0): React.ReactNode => {
    return items.map((item) => {
      if (item.isTitle) {
        return (
          <div
            key={item.label}
            className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase"
          >
            {item.label}
          </div>
        );
      }

      const hasSubItems = item.subItems && item.subItems.length > 0;
      const isExpanded = item.id && expandedMenus.has(item.id);
      const isActive = isMenuActive(item.link);

      return (
        <div key={item.id || item.label}>
          <div
            className={`flex items-center justify-between px-4 py-2.5 text-sm font-medium transition-colors ${
              isActive
                ? 'bg-custom-500/10 text-custom-500'
                : 'text-slate-700 hover:bg-slate-100'
            } ${hasSubItems ? 'cursor-pointer' : ''}`}
            onClick={() => hasSubItems && item.id && toggleMenu(item.id)}
          >
            <Link
              to={item.link || '#'}
              className="flex items-center gap-3 flex-1"
              onClick={(e) => hasSubItems && e.preventDefault()}
            >
              {item.icon && <span className="w-5 h-5">{item.icon}</span>}
              <span>{item.label}</span>
            </Link>
            {hasSubItems && (
              <span
                className={`transition-transform ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              >
                ▼
              </span>
            )}
          </div>

          {hasSubItems && isExpanded && (
            <div className="bg-slate-50 border-l-2 border-slate-200">
              {renderMenuItems(item.subItems || [], level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className="flex h-screen bg-slate-100">
      <div
        className={`fixed inset-y-0 left-0 transition-all duration-300 ease-linear bg-white border-r border-slate-200 z-[1003] ${
          sidebarOpen ? 'w-72' : 'w-0 overflow-hidden'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="h-header flex items-center justify-between px-4 py-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900">
              Admin page
            </h3>
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden p-1 hover:bg-slate-100 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {renderMenuItems(menuData)}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col transition-all duration-300 ${
          sidebarOpen ? 'ml-72' : 'ml-0'
        }`}
      >
        {/* Top Bar */}
        <div className="h-header bg-white border-b border-slate-200 px-6 py-4 flex items-center">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-slate-100 rounded"
          >
            <Menu className="w-5 h-5 text-slate-700" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">{children}</div>
      </div>
    </div>
  );
};

export default AdminLayout;
